import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../config/api'
import { isSessionTokenViable } from '../config/sessionToken'
import { useFeatureFlags } from './FeatureFlagContext'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(new Set())
  const [roles, setRoles] = useState([])
  const [venueScope, setVenueScope] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { setFlagsFromLogin, clearFlags, refreshFlags } = useFeatureFlags()

  const hydrateFromUserData = (userData) => {
    setUser(userData)
    setPermissions(new Set(Array.isArray(userData.permissions) ? userData.permissions : []))
    setRoles(Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : []))
    setVenueScope(userData.venueScope || null)
  }

  const clearSession = () => {
    setUser(null)
    setPermissions(new Set())
    setRoles([])
    setVenueScope(null)
    localStorage.removeItem('paysafego_user')
  }

  useEffect(() => {
    const storedUser = localStorage.getItem('paysafego_user')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        if (!isSessionTokenViable(userData?.token)) {
          clearSession()
        } else {
          hydrateFromUserData(userData)
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        clearSession()
      }
    }
    setLoading(false)
  }, [])

  // Re-sync feature flags with the server whenever we have a session (fixes stale
  // paysafego_feature_flags in localStorage and wrong tier when host has no tenant slug).
  useEffect(() => {
    if (loading || !user?.token) return
    if (typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '/login') return
    refreshFlags()
  }, [loading, user?.token, refreshFlags])

  const readStoredFeatureFlags = () => {
    try {
      const stored = localStorage.getItem('paysafego_feature_flags')
      return stored ? JSON.parse(stored) : {}
    } catch (_) {
      return {}
    }
  }

  const finalizeLoginResult = async (j) => {
    const result = applyLoginPayload(j)
    refreshFlags().catch(() => {})
    return { ...result, featureFlags: j.featureFlags || readStoredFeatureFlags() }
  }

  const login = async (credentials) => {
    try {
      // Clear stale session so patched fetch does not send Bearer; avoids 401 from JWT layer on /api/login
      clearSession()
      const res = await fetch(getLocalApiUrl('/api/login'), { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(credentials || {}) })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || j.message || 'Login failed')
      if (j.mfaRequired && j.mfaToken) {
        return {
          success: true,
          mfaRequired: true,
          mfaToken: j.mfaToken,
          user: j.user || { username: credentials?.username },
        }
      }
      return await finalizeLoginResult(j)
    } catch (e) { return { success:false, error: e.message } }
  }

  const applyLoginPayload = (j) => {
      const userData = {
        ...j.user,
        token: j.token,
        permissions: j.permissions || [],
        roles: j.roles || (j.user?.role ? [j.user.role] : []),
        venueScope: j.venueScope || null,
      }
      hydrateFromUserData(userData)
      localStorage.setItem('paysafego_user', JSON.stringify(userData))
      if (j.featureFlags != null && typeof j.featureFlags === 'object') {
        setFlagsFromLogin(j.featureFlags, j.isClub)
      } else if (typeof j.isClub === 'boolean') {
        setFlagsFromLogin({}, j.isClub)
      }
      return { success: true, user: userData, featureFlags: j.featureFlags || {} }
  }

  const completeMfaLogin = async ({ mfaToken, code }) => {
    try {
      clearSession()
      const res = await fetch(getLocalApiUrl('/api/login/mfa'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, code }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || j.message || 'Verification failed')
      return await finalizeLoginResult(j)
    } catch (e) {
      return { success: false, error: e.message }
    }
  }

  const logout = () => {
    const username = user?.username || user?.sub
    if (user?.token) {
      authFetch(getLocalApiUrl('/api/logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(username ? { username } : {}),
      }).catch(() => {})
    }
    setUser(null)
    setPermissions(new Set())
    setRoles([])
    setVenueScope(null)
    localStorage.removeItem('paysafego_user')
    clearFlags()
    toast.success('Logged out successfully')
    navigate('/login')
  }

  const isAuthenticated = () => !!user

  /** Matches backend authz: full access regardless of perms array (super_admin often has no rows in role_permissions). */
  const hasFullAccessRole = () => {
    if (!user) return false
    if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'org_admin') return true
    if (roles.includes('admin') || roles.includes('super_admin') || roles.includes('org_admin')) return true
    return false
  }

  // Strict check: only the exact matrix cell (or a wildcard that covers it). No legacy/cross-row aliases.
  const canExact = (resource, action) => {
    if (hasFullAccessRole()) return true
    if (!permissions || permissions.size === 0) return false
    if (permissions.has(`${resource}:${action}`) || permissions.has(`${resource}:*`) || permissions.has(`*:${action}`) || permissions.has('*:*')) return true
    for (const entry of permissions) {
      const i = String(entry).lastIndexOf(':')
      if (i <= 0) continue
      const gr = entry.slice(0, i)
      const ga = entry.slice(i + 1)
      if (ga !== action && ga !== '*') continue
      if (gr.endsWith('/*') && resource.startsWith(`${gr.slice(0, -2)}/`)) return true
    }
    return false
  }

  // Fine-grained permission check: can('profile-safe/search', 'view')
  // Mirrors backend authz.permissionAllowed (including module/*:action segment wildcards).
  const can = (resource, action) => {
    if (hasFullAccessRole()) return true
    if (!permissions || permissions.size === 0) return false
    const key = `${resource}:${action}`
    if (permissions.has(key)) return true
    // Legacy platform key `approvals:*` ↔ tenant matrix `admin/approvals:*`
    if (resource === 'approvals') {
      const mappedAction = action === 'decide' ? 'approve' : action
      if (permissions.has(`admin/approvals:${mappedAction}`) || permissions.has('admin/approvals:manage')) return true
    }
    if (resource === 'admin/approvals' && (action === 'approve' || action === 'manage') && (permissions.has('approvals:*') || permissions.has('approvals:manage'))) return true
    if (resource === 'profile-safe/management') {
      if (
        action === 'view' &&
        (permissions.has('profile-safe/search:view') ||
          permissions.has('profile-safe/search:manage') ||
          permissions.has('profile-safe/search:*'))
      ) {
        return true
      }
    }
    if (resource === 'profile-safe/search' && action === 'view') {
      if (permissions.has('profile-safe/management:view') || permissions.has('profile-safe/management:*')) return true
      if (permissions.has('profile-safe/search:manage') || permissions.has('profile-safe/search:*')) return true
    }
    if (resource === 'profile-safe/search' && action === 'edit') {
      if (permissions.has('profile-safe/management:edit') || permissions.has('profile-safe/management:*')) return true
    }
    if (resource === 'profile-safe/management' && action === 'edit') {
      if (permissions.has('profile-safe/search:edit')) return true
    }
    if (resource === 'governance.checklist') {
      if (permissions.has(`governance-safe/checklist:${action}`) || permissions.has('governance-safe/checklist:*')) return true
    }
    if (resource === 'governance-safe/checklist') {
      if (permissions.has(`governance.checklist:${action}`) || permissions.has('governance.checklist:*')) return true
    }
    if (resource === 'governance.risk') {
      if (permissions.has(`governance-safe/risk:${action}`) || permissions.has('governance-safe/risk:*')) return true
    }
    if (resource === 'governance-safe/risk') {
      if (permissions.has(`governance.risk:${action}`) || permissions.has('governance.risk:*')) return true
    }
    if (resource === 'rules') {
      const grants = {
        view: ['monitor-safe/builder:view', 'monitor-safe/builder:edit', 'monitor-safe/builder:manage', 'monitor-safe/rule-scheduler:view', 'monitor-safe/rule-scheduler:edit', 'monitor-safe/rule-scheduler:manage', 'monitor-safe/testing:view', 'monitor-safe/testing:manage'],
        edit: ['monitor-safe/builder:edit', 'monitor-safe/builder:manage', 'monitor-safe/rule-scheduler:edit', 'monitor-safe/rule-scheduler:manage'],
        manage: ['monitor-safe/builder:manage', 'monitor-safe/rule-scheduler:manage'],
      }
      if ((grants[action] || []).some((k) => permissions.has(k))) return true
    }
    // Legacy users:* no longer grants User Management — the matrix row admin/user-management is the source of truth.
    // Employees stays on admin/employees:* only — do not alias from user-management or staff.
    if (resource === 'payout-safe/capture') {
      if (permissions.has(`mobile/payout:${action}`) || permissions.has('mobile/payout:*')) return true
      if (action === 'view' && (permissions.has('mobile/payout:create') || permissions.has('mobile/payout:view'))) return true
      if (action === 'create' && permissions.has('mobile/payout:create')) return true
    }
    const wildcards = [`${resource}:*`, `*:${action}`, '*:*']
    if (wildcards.some(k => permissions.has(k))) return true
    for (const entry of permissions) {
      const i = String(entry).lastIndexOf(':')
      if (i <= 0) continue
      const gr = entry.slice(0, i)
      const ga = entry.slice(i + 1)
      if (ga !== action && ga !== '*') continue
      if (gr.endsWith('/*')) {
        const prefix = gr.slice(0, -2)
        if (resource === prefix || (prefix && resource.startsWith(`${prefix}/`))) return true
      }
    }
    return false
  }

  // Backwards-compatible role check — checks if user has a specific role
  const hasRole = (roleName) => {
    if (!user) return false
    return roles.includes(roleName) || user.role === roleName
  }

  // Legacy hasPermission — now delegates to can() or role check
  const hasPermission = (requiredRole) => {
    if (!user) return false
    // If it looks like a permission string (contains ':'), use can()
    if (requiredRole && requiredRole.includes(':')) {
      const i = requiredRole.lastIndexOf(':')
      const resource = requiredRole.slice(0, i)
      const action = requiredRole.slice(i + 1)
      return can(resource, action)
    }
    // Otherwise treat as role name — platform roles have all access
    if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'org_admin' ||
        roles.includes('admin') || roles.includes('super_admin') || roles.includes('org_admin')) return true
    return hasRole(requiredRole)
  }

  // Venue scope helpers
  const hasVenueAccess = (venueId) => {
    if (!venueScope) return true // no scope = unrestricted
    if (venueScope === '*' || venueScope === 'all') return true
    if (Array.isArray(venueScope)) return venueScope.includes(venueId)
    return false
  }

  const getAllowedVenues = () => {
    if (!venueScope || venueScope === '*' || venueScope === 'all') return null // null = all venues
    return Array.isArray(venueScope) ? venueScope : []
  }

  const value = {
    user,
    login,
    completeMfaLogin,
    logout,
    isAuthenticated,
    hasPermission,
    hasRole,
    can,
    canExact,
    permissions,
    roles,
    venueScope,
    hasVenueAccess,
    getAllowedVenues,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
